import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { GET_REFRESH_TOKEN } from '../api/apiPath';
import http from '../api/server';
import { toast } from 'react-toastify';
import { logoutUser } from '../store/slices/UserSlice';

const useTokenRefresh = () => {
  const { userObj } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const tokenRefreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  /**
   * Refresh the authentication token
   */
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      return; // Prevent multiple simultaneous refresh attempts
    }

    isRefreshingRef.current = true;
    try {
    const params = new URLSearchParams();
    params.append("refresh", userObj?.refreshToken);
    params.append("clientType", "web");
    const queryString = params.toString();
      const response = await http.post(GET_REFRESH_TOKEN + `?${queryString}` );
      
      if (response?.data?.authToken) {
        // Update the token in localStorage by updating the Redux store
        // The token is stored in persist:root -> user -> userObj -> token
        const persistedData = localStorage.getItem('persist:root');
        if (persistedData) {
          const parsed = JSON.parse(persistedData);
          const userData = JSON.parse(parsed.user);
          
          // Update token and expiration and refreshtoken
          userData.userObj.token = response.data.authToken;
          if (response.data.refreshExpiresAt) {
            userData.userObj.expiresAt = response.data.refreshExpiresAt;
          }
          if (response.data.refreshToken) {
            userData.userObj.refreshToken = response.data.refreshToken;
          }
          
          parsed.user = JSON.stringify(userData);
          localStorage.setItem('persist:root', JSON.stringify(parsed));
        }

        // toast.success('Token refreshed successfully', {
        //   autoClose: 2000,
        //   position: 'bottom-right',
        // });

        // Schedule next refresh
        scheduleTokenRefresh(response.data.refreshExpiresAt);
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      toast.error('Token refresh failed. Please login again.', {
        autoClose: 2000,
        position: 'top-center',
      });
      // logout on refresh failure
      setTimeout(() => {
                dispatch(logoutUser({
                  userId: userObj?.userId,
                  userIdentifier: userObj?.userEmail,
                }));
            }, 2000);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [dispatch]);

  /**
   * Schedule token refresh for 10 minutes before expiration
   */
  const scheduleTokenRefresh = useCallback(
    (expiresAt) => {
      // Clear existing timer
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
      }

      if (!expiresAt) {
        return;
      }

      try {
        const expirationTime = new Date(expiresAt).getTime();
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;
        
        // Refresh 10 minutes (600000 ms) before expiration
        const refreshTime = timeUntilExpiry - 10 * 60 * 1000; 
        // console.log("refresh time", refreshTime, expiresAt, timeUntilExpiry, expirationTime, currentTime);

        if (refreshTime > 0) {
          tokenRefreshTimerRef.current = setTimeout(() => {
            console.log('Triggering token refresh (10 minutes before expiry)');
            refreshToken();
          }, refreshTime);
        } else if (timeUntilExpiry > 0) {
          // If less than 10 minutes remain, refresh immediately
          console.log('Token expiring soon, refreshing now');
          refreshToken();
        }
      } catch (error) {
        console.error('Error scheduling token refresh:', error);
      }
    },
    [refreshToken]
  );

  /**
   * Initialize token refresh monitoring
   */
  useEffect(() => {
    if (userObj?.token && userObj?.expiresAt) {
      scheduleTokenRefresh(userObj.expiresAt);
    }

    return () => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
      }
    };
  }, [userObj?.token, userObj?.expiresAt, scheduleTokenRefresh]);

  return { refreshToken };
};

export default useTokenRefresh;
